import { useEffect, useMemo } from "react"
import { PublicUser, UserRef } from "@/types/user"
import { useInView } from "react-intersection-observer"

import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import { selectPublicUsersByRef } from "@/lib/storage/public-user-slice"
import { fetchPublicUsers } from "@/lib/storage/public-user-thunks"
import { AppDispatch } from "@/lib/storage/redux-store"

const pendingUserRequests = new Set<UserRef>()
const requestQueue = new Set<UserRef>()
let batchTimeout: NodeJS.Timeout | null = null
let globalDispatch: AppDispatch | null = null

const processBatch = () => {
  if (requestQueue.size === 0 || !globalDispatch) return

  const userRefsToFetch = Array.from(requestQueue)
  requestQueue.clear()

  userRefsToFetch.forEach((userRef) => pendingUserRequests.add(userRef))

  void globalDispatch(fetchPublicUsers({ userRefs: userRefsToFetch })).finally(
    () =>
      userRefsToFetch.forEach((userRef) => pendingUserRequests.delete(userRef))
  )
}

const queueUserRequest = (userRef: UserRef, dispatch: AppDispatch) => {
  globalDispatch = dispatch

  if (pendingUserRequests.has(userRef)) return

  requestQueue.add(userRef)

  if (batchTimeout) clearTimeout(batchTimeout)
  batchTimeout = setTimeout(processBatch, 10)
}

export function usePublicUsers(userRefs: UserRef[]) {
  const dispatch = useAppDispatch()
  const publicUsersByRef = useAppSelector(selectPublicUsersByRef)

  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: "50px",
  })

  const { users, missingUserRefs } = useMemo(() => {
    const users: PublicUser[] = []
    const missingUserRefs: UserRef[] = []

    userRefs.forEach((userRef) => {
      const user = publicUsersByRef.get(userRef)
      if (user) {
        users.push(user)
      } else {
        missingUserRefs.push(userRef)
      }
    })

    return { users, missingUserRefs }
  }, [userRefs, publicUsersByRef])

  useEffect(() => {
    if (inView && missingUserRefs.length > 0) {
      missingUserRefs.forEach((userRef) => {
        queueUserRequest(userRef, dispatch)
      })
    }
  }, [dispatch, inView, missingUserRefs])

  return { users, missingUserRefs, ref }
}
